// University of the Pacific: private college page (#2839), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Pacific's Common Data Set 2025-2026 (Office of Institutional Research,
//    the full-form PDF on pacific.edu, newest on its Common Data Set page):
//    C1 applicants, admits and enrollees for Fall 2025 with the residency
//    table (its Unknown column has counts, so it is kept), C8 test policy for
//    Fall 2027, C21/C22 (no early decision; nonbinding early action closing
//    November 15, notification December 20, no counts). Its G section marks
//    2026-2027 costs as not available when filed ("5/1/2026"), so the cost
//    table does not use it.
//  - pacific.edu Undergraduate Students Admission (First-Year Student
//    Deadlines, the list for the application that opened July 1, 2026; the
//    page names no entry year, and the CDS C8 names fall 2027), Early
//    Action, Test Scores, Interviews and Auditions. Departure: the Early
//    Action page says early applicants hear "by mid-January", while the
//    deadlines list and the CDS give December 20; the page uses December 20.
//  - Financial Aid, Undergraduate Student Cost (full-time, on campus). The
//    page names no year; its tuition ($59,560) and fees ($1,200) match the
//    Undergraduate Tuition and Fees page's "Fall 2026 | Spring 2027" rates
//    ($29,780 tuition and $600 in Stockton fees a semester,
//    pacific.edu/financial-aid/cost-of-attendance/tuition-fees), so it is
//    read as 2026-2027. It gives direct and indirect subtotals ($78,536 and
//    $5,755) but no grand total; the total here is their sum.
//  - College Scorecard API, school id 120883 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Pacific Common Data Set 2025-2026',
        url: 'https://www.pacific.edu/about-pacific/administrative-offices/institutional-research/common-data-set',
        read: '2026-10-01',
    },
    admission: {
        label: 'Pacific Undergraduate Students Admission, First-Year Student Deadlines',
        url: 'https://www.pacific.edu/admission/undergraduate',
        read: '2026-10-01',
    },
    earlyAction: {
        label: 'Pacific Undergraduate Admission, Early Action',
        url: 'https://www.pacific.edu/admission/undergraduate/early-action',
        read: '2026-10-01',
    },
    testScores: {
        label: 'Pacific Undergraduate Admission, Test Scores',
        url: 'https://www.pacific.edu/admission/undergraduate/test-scores',
        read: '2026-10-01',
    },
    auditions: {
        label: 'Pacific Undergraduate Admission, Interviews and Auditions',
        url: 'https://www.pacific.edu/admission/undergraduate/interviews-auditions',
        read: '2026-10-01',
    },
    coa: {
        label: 'Pacific Financial Aid, Undergraduate Student Cost',
        url: 'https://www.pacific.edu/financial-aid/cost-of-attendance/undergraduate-students',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?120883-University-of-the-Pacific',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-the-pacific',
    kbId: 'university_of_the_pacific',
    name: 'University of the Pacific',
    shortName: 'Pacific',
    aliases: ['UOP', 'Pacific'],
    location: 'Stockton, CA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Pacific brand guidelines); decoration only.
    branding: {
        primary: '#FF671D',
        secondary: '#0F0F0F',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_the_pacific_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 8656,
        admitted: 5977,
        enrolled: 676,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 5818, admitted: 4405 },
            { group: 'Out-of-state', applied: 2421, admitted: 1285 },
            { group: 'International', applied: 348, admitted: 242 },
            { group: 'Residency unknown', applied: 69, admitted: 45 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Pacific\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'Pacific has no early decision plan. Its Early Action plan is nonbinding and not restrictive. The Common Data Set gives no early action counts, so it gives no early round acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Pacific as a whole. A few programs ask for more:',
        rows: [
            { school: 'Conservatory of Music', requirement: 'Every music major except Music Industry Studies requires an audition with the Conservatory.', source: 'auditions' },
            { school: 'Pre-Pharmacy Advantage and Pre-Dentistry Advantage programs', requirement: 'Apply by November 15, the Early Action deadline; it is the required deadline for both programs.', source: 'earlyAction' },
        ],
        source: 'auditions',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Action', deadline: 'November 15', decision: 'December 20', binding: false },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'March 31', binding: false },
        ],
        note: 'Applications after January 15 are considered as space allows. January 15 is also the final deadline for the Powell Scholars Program and the First-Year Honors program. The enrollment confirmation deadline is May 1.',
        source: 'admission',
        binding: 'Early Action is not binding. Pacific describes it as a plan for applicants with exceptionally strong academic records who consider Pacific their top choice; most who apply Early Action without meeting its criteria are considered with the regular group decided March 31.',
        bindingSource: 'earlyAction',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Pacific is test-optional: you can send SAT or ACT scores if you want to share them, and your application is complete without them. Pacific says applicants who do not send scores are not disadvantaged in any way in the review.',
        source: 'testScores',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required but considered if submitted for fall 2027 applicants, and says scores can help place students in the right math and English course.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'admission',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 59560 },
            { item: 'Fees', amount: 1200 },
            { item: 'Housing (on campus)', amount: 9876 },
            { item: 'Food', amount: 7900 },
            { item: 'Books and supplies', amount: 1305 },
            { item: 'Transportation', amount: 1586 },
            { item: 'Personal expenses', amount: 2864 },
        ],
        total: 84291,
        note: 'For a full-time undergraduate living on campus, fall and spring. Pacific lists $78,536 billed by the university and $5,755 in other expected costs; the total is the two together. Pacific does not label the year on this page; its tuition matches the published rate for fall 2026 and spring 2027.',
        source: 'coa',
        netPrice: {
            average: 24186,
            byIncome: [
                { income: '$0 to $30,000', amount: 17568 },
                { income: '$30,001 to $48,000', amount: 20526 },
                { income: '$48,001 to $75,000', amount: 19519 },
                { income: '$75,001 to $110,000', amount: 24916 },
                { income: 'Over $110,000', amount: 35741 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Pacific\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
